// Summary card (live "Today so far" / past "Game summary").
// Spotlight on one topic (big number, one sentence, a chart of the day that fills the space),
// with all topics always visible as tiles underneath. Rotates automatically, most important first.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Icon from '../../../components/atoms/Icon';
import { STATUS_COLORS } from '../concessionTheme';
import { formatDuration, formatClock } from './liveMetrics';
import { Sparkline, unitFormatter } from './LiveComponents';
import '../concession.css';

const ROTATE_MS = 8000;
const TICK_MS = 100;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

// When and how far over target? Area chart split at the target line (red above, green below),
// a slim on/over/overflow band underneath, and hover for time + value + state. Grows to fill the card.
const STATE_LABELS = { good: 'On target', over: 'Over target', overflow: 'Overflow' };

const OverTargetChart = ({ series, threshold, formatValue, markers = [], atOrAbove = false }) => {
  const isOver = (v) => (atOrAbove ? Math.round(v) >= threshold : v > threshold);
  const uid = React.useId().replace(/:/g, '');
  const [hover, setHover] = useState(null);
  if (!series || series.length < 2) return <div className="cx-spark-empty">Not enough data yet</div>;

  const t0 = series[0].t;
  const t1 = series[series.length - 1].t;
  const span = Math.max(1, t1 - t0);
  const maxY = Math.max(threshold * 1.5, ...series.map((p) => p.y)) * 1.1;
  const x = (t) => ((t - t0) / span) * 100;
  const y = (v) => 100 - (v / maxY) * 100;
  const yT = y(threshold);
  const line = series.map((p) => `${x(p.t).toFixed(2)},${y(p.y).toFixed(2)}`).join(' ');
  const area = `0,100 ${line} 100,100`;
  const stateOf = (p) => (p.o ? 'overflow' : isOver(p.y) ? 'over' : 'good');

  // Runs for the band and the overflow segments
  const runs = [];
  series.forEach((p, i) => {
    const state = stateOf(p);
    const end = series[i + 1] ? series[i + 1].t : p.t + 60000;
    const last = runs[runs.length - 1];
    if (last && last.state === state) last.end = end;
    else runs.push({ state, start: p.t, end });
  });
  const overflowSegments = [];
  let current = null;
  series.forEach((p, i) => {
    if (p.o) {
      if (!current) {
        current = i > 0 ? [series[i - 1], p] : [p];
        overflowSegments.push(current);
      } else current.push(p);
    } else if (current) {
      current.push(p);
      current = null;
    }
  });

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const target = t0 + Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * span;
    let best = series[0];
    series.forEach((p) => {
      if (Math.abs(p.t - target) < Math.abs(best.t - target)) best = p;
    });
    setHover(best);
  };
  const hx = hover ? x(hover.t) : 0;

  return (
    <div className="cx-sc-chart">
      <div className="cx-sc-chart-plot cx-ot-plot" onPointerMove={handleMove} onPointerLeave={() => setHover(null)}>
        <svg className="cx-spark" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
          <defs>
            <clipPath id={`above-${uid}`}><rect x="0" y="0" width="100" height={yT} /></clipPath>
            <clipPath id={`below-${uid}`}><rect x="0" y={yT} width="100" height={100 - yT} /></clipPath>
          </defs>
          <polygon points={area} className="cx-ot-area-over" clipPath={`url(#above-${uid})`} />
          <polygon points={area} className="cx-ot-area-good" clipPath={`url(#below-${uid})`} />
          <line x1="0" x2="100" y1={yT} y2={yT} className="cx-spark-target" vectorEffect="non-scaling-stroke" />
          <polyline points={line} className="cx-ot-line" vectorEffect="non-scaling-stroke" />
          {overflowSegments.filter((run) => run.length > 1).map((run, i) => (
            <polyline
              key={i}
              points={run.map((p) => `${x(p.t).toFixed(2)},${y(p.y).toFixed(2)}`).join(' ')}
              className="cx-spark-overflow"
              vectorEffect="non-scaling-stroke"
            />
          ))}
        </svg>
        <span className="cx-ot-target-label" style={{ top: `${yT}%` }}>Target</span>
        {markers
          .filter((m) => m.t >= t0 && m.t <= t1)
          .map((m) => (
            <div key={m.label + m.t} className="cx-spark-marker" style={{ left: `${x(m.t)}%` }} />
          ))}
        {hover && (
          <>
            <div className="cx-spark-guide" style={{ left: `${hx}%` }} />
            <div
              className={`cx-spark-dot${hover.o ? ' overflow' : ''}`}
              style={{ left: `${hx}%`, top: `${y(hover.y)}%`, color: isOver(hover.y) || hover.o ? 'var(--q-bad)' : 'var(--q-good)' }}
            />
            <div className={`cx-spark-tip${hx > 60 ? ' left' : ''}`} style={{ left: `${hx}%` }}>
              <span className="cx-spark-tip-time">{formatClock(hover.t)}</span>
              <span className={`cx-spark-tip-value${hover.o ? ' overflow' : ''}`}>
                {formatValue(hover.y, !!hover.o)} · {STATE_LABELS[stateOf(hover)]}
              </span>
            </div>
          </>
        )}
      </div>
      {/* Slim band: green on target, red over, hatched overflow */}
      <div className="cx-ot-band" aria-hidden="true">
        {runs.map((r) => (
          <div
            key={r.start}
            className={`cx-sc-run cx-sc-run-${r.state}`}
            style={{ left: `${x(r.start)}%`, width: `${((r.end - r.start) / span) * 100}%` }}
          />
        ))}
      </div>
      <TimeAxis t0={t0} t1={t1} markers={markers} />
    </div>
  );
};

// Time axis under a summary chart: start time, end time (always shown: in a live game it's "now"),
// and marker labels (e.g. "Game start") under their lines.
// Near the start, the start time gives way to the label. Near the end, the label sits left of its
// line; right at the end (no room) only the line is shown.
const TimeAxis = ({ t0, t1, markers = [] }) => {
  const span = Math.max(1, t1 - t0);
  const placed = markers
    .filter((m) => m.t >= t0 && m.t <= t1)
    .map((m) => ({ ...m, pct: ((m.t - t0) / span) * 100 }))
    .filter((m) => m.pct <= 90);
  const nearStart = placed.some((m) => m.pct < 22);
  return (
    <div className="cx-sc-axis cx-sc-axis-markers">
      <span style={{ visibility: nearStart ? 'hidden' : 'visible' }}>{formatClock(t0)}</span>
      {placed.map((m) => (
        <span
          key={m.label + m.t}
          className={`cx-sc-axis-marker${m.pct > 65 ? ' before-line' : m.pct < 15 ? ' edge-left' : ''}`}
          style={{ left: `${m.pct}%` }}
        >
          {m.label}
        </span>
      ))}
      <span>{formatClock(t1)}</span>
    </div>
  );
};

// Chart of the day that grows to fill the card
// `valueColor` (optional) colors each stretch of the line by its own value
const DayChart = ({ series, color, target, formatValue, markers, valueColor }) => (
  <div className="cx-sc-chart">
    <div className="cx-sc-chart-plot">
      <Sparkline points={series} color={valueColor ? 'var(--q-text-subtle)' : color} valueColor={valueColor} target={target} height="100%" minMax={target ? target * 1.5 : 0} formatValue={formatValue} markers={markers} markerLabel="none" />
    </div>
    {series && series.length > 1 && (
      <TimeAxis t0={series[0].t} t1={series[series.length - 1].t} markers={markers} />
    )}
  </div>
);

/*
  queue:       { title, avg, peak, peakAt, peakOverflow, avgStatus, peakStatus, target, unit }
  time:        { total, over, overflow, overflowEpisodes, overflowSections, overLabel }
  delta:       { avg, peak, peakAt } | null
  transaction: { avg, peak, peakAt, format } | null
  series:      { main, txn, delta } day series ({ t, y, o })
  overThreshold: value above which a minute counts as over target / busy
*/
// overAtOrAbove: queues count "over" from the threshold itself (10 min is over); counts from above it
const DaySummary = ({ queue, time, delta, transaction, series = {}, overThreshold, overAtOrAbove = false, markers = [] }) => {
  const isCount = queue.target === undefined;
  const sharePct = time.total > 0 ? Math.round((time.over / time.total) * 100) : 0;
  const around = (at) => (at ? ` around ${at}` : '');
  const valueFormat = unitFormatter(isCount ? 'people' : 'min');

  // ---- Topics ----
  const queueTopic = {
    key: 'queue',
    label: isCount ? 'Count' : 'Queue',
    tileValue: <>{queue.avg}<small>{isCount ? ' ppl' : ' min'}</small></>,
    tileColor: STATUS_COLORS[queue.avgStatus],
    value: queue.avg,
    unitText: queue.unit,
    caption: isCount ? 'average people in line' : 'average queue',
    color: STATUS_COLORS[queue.avgStatus],
    line: isCount
      ? `Busiest: ${queue.peak} people${around(queue.peakAt)}`
      : `Peak ${queue.peak}${queue.peakOverflow ? '+' : ''} min${around(queue.peakAt)} · ${
          queue.avg <= queue.target ? `under the ${queue.target} min target on average` : `${queue.avg - queue.target} min over target on average`
        }`,
    visual: <DayChart series={series.main} color={STATUS_COLORS[queue.avgStatus]} valueColor={queue.statusFor ? (v) => STATUS_COLORS[queue.statusFor(v)] : undefined} target={queue.target} formatValue={valueFormat} markers={markers} />,
  };

  const timeTopic = {
    key: 'time',
    label: isCount ? 'Busy time' : 'Over target',
    alert: time.over > 0,
    alertIcon: 'exclamation-circle-fill',
    tileValue: time.over > 0 ? formatDuration(time.over) : 'None',
    tileColor: time.over > 0 ? STATUS_COLORS.bad : STATUS_COLORS.good,
    value: time.over > 0 ? formatDuration(time.over) : 'None',
    caption: time.over > 0
      ? `${isCount ? 'busy' : 'over target'} · ${sharePct}% of the time`
      : isCount ? 'never busy' : 'every minute was on target',
    color: time.over > 0 ? STATUS_COLORS.bad : STATUS_COLORS.good,
    line: time.overflow > 0 ? (
      <span className="cx-ds-line-alert">
        <Icon name="exclamation-triangle-fill" />
        {formatDuration(time.overflow)} in overflow
        {time.overflowSections.length > 0 && ` · Section${time.overflowSections.length > 1 ? 's' : ''} ${time.overflowSections.join(', ')}`}
      </span>
    ) : null,
    visual: <OverTargetChart series={series.main} threshold={overThreshold} atOrAbove={overAtOrAbove} formatValue={valueFormat} markers={markers} />,
  };

  const deltaTopic = delta && {
    key: 'delta',
    label: 'Delta',
    tileValue: <>{delta.avg}<small> min</small></>,
    value: delta.avg,
    unitText: 'min',
    caption: 'average gap between the longest and shortest queue',
    line: `Widest gap: ${delta.peak} min${around(delta.peakAt)}`,
    visual: <DayChart series={series.delta} color="var(--q-text-muted)" formatValue={unitFormatter('min')} markers={markers} />,
  };

  const transactionTopic = transaction && {
    key: 'transaction',
    label: 'Transactions',
    tileValue: transaction.format(transaction.avg),
    value: transaction.format(transaction.avg),
    caption: 'average time per transaction',
    line: `Slowest: ${transaction.format(transaction.peak)}${around(transaction.peakAt)}`,
    visual: <DayChart series={series.txn} color="var(--q-brand)" formatValue={(v) => transaction.format(v)} markers={markers} />,
  };

  // Fixed order so tiles never move; when overflow happened or lots of time was over target,
  // the rotation starts on "Over target" instead of "Queue"
  const timeIsUrgent = time.overflow > 0 || (time.total > 0 && time.over / time.total >= 0.25);
  const topics = [queueTopic, timeTopic, deltaTopic, transactionTopic].filter(Boolean);
  const startKey = timeIsUrgent ? 'time' : 'queue';
  const topicKeys = topics.map((t) => t.key).join('|');

  // ---- Rotation state ----
  const [activeKey, setActiveKey] = useState(startKey);
  const [elapsed, setElapsed] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reducedMotion = useMemo(prefersReducedMotion, []);
  const containerRef = useRef(null);

  // Jump to the starting topic when urgency changes; keep the active topic valid
  useEffect(() => {
    setActiveKey(startKey);
    setElapsed(0);
  }, [startKey]);
  useEffect(() => {
    if (!topics.some((t) => t.key === activeKey)) setActiveKey(startKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicKeys]);

  const paused = hovered || focused || reducedMotion || topics.length < 2;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      setElapsed((e) => e + TICK_MS);
    }, TICK_MS);
    return () => clearInterval(id);
  }, [paused]);

  useEffect(() => {
    if (elapsed < ROTATE_MS) return;
    const index = topics.findIndex((t) => t.key === activeKey);
    setActiveKey(topics[(index + 1) % topics.length].key);
    setElapsed(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elapsed]);

  const select = (key) => {
    setActiveKey(key);
    setElapsed(0);
  };

  const active = topics.find((t) => t.key === activeKey) || topics[0];

  return (
    <div
      ref={containerRef}
      className="cx-sc"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(e) => {
        if (!containerRef.current?.contains(e.relatedTarget)) setFocused(false);
      }}
    >
      {/* Spotlight: every topic shares one grid cell so the card never changes height */}
      <div className="cx-sc-stage" aria-live="off">
        {topics.map((t) => (
          <div key={t.key} className={`cx-sc-spot${t.key === active.key ? ' active' : ''}`} aria-hidden={t.key !== active.key}>
            <div className="cx-sc-eyebrow">
              {t.alert && <Icon name={t.alertIcon || 'exclamation-triangle-fill'} className="cx-sc-eyebrow-alert" />}
              {t.label}
            </div>
            <div className="cx-ds-big">
              <span className="cx-ds-big-value" style={t.color ? { color: t.color } : undefined}>{t.value}</span>
              {t.unitText && <span className="cx-ds-big-unit">{t.unitText}</span>}
            </div>
            <div className="cx-ds-caption">{t.caption}</div>
            {t.line && <div className="cx-ds-line">{t.line}</div>}
            <div className="cx-sc-visual">{t.visual}</div>
          </div>
        ))}
      </div>

      {/* Full-width countdown to the next topic */}
      <div className="cx-ds-progress cx-sc-progress" aria-hidden="true">
        <div
          className={`cx-ds-progress-fill${paused ? ' paused' : ''}`}
          style={{ width: `${Math.min(100, (elapsed / ROTATE_MS) * 100)}%` }}
        />
      </div>

      {/* Every topic at a glance; click to focus */}
      <div className="cx-sc-tiles" role="tablist" aria-label="Summary topics">
        {topics.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={t.key === active.key}
            className={`cx-sc-tile${t.key === active.key ? ' active' : ''}`}
            onClick={() => select(t.key)}
          >
            <span className="cx-sc-tile-label">
              {t.alert && <Icon name={t.alertIcon || 'exclamation-triangle-fill'} className="cx-sc-tile-alert" />}
              {t.label}
            </span>
            <span className="cx-sc-tile-value" style={t.tileColor ? { color: t.tileColor } : undefined}>
              {t.tileValue}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default DaySummary;
